import { TabStyles } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { getAppStore } from '~src/app/AppStore';
import { DataQualityTriggerConfigurationUIAction, DataQualityTriggerConfigurationUIProps } from '~src/app/quality/DataQualityTriggerDialog';
import { DataQualityTriggerUberDialog } from '~src/app/quality/DataQualityTriggerUberDialog';
import { SessionSiteSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { FlowOutputSelector } from '~src/flow/components/FlowOutputSelector';
import { FlowParametersDropdown, FlowParametersDropdownProps } from '~src/flow/components/FlowParametersDropdown';
import { FlowSubscriptionCountTooltip } from '~src/flow/components/FlowSubscriptionCount';
import { Flows } from '~src/flow/resources/Flows';
import { FlowParameter } from '~src/flow/types/FlowParameters';
import { areFlowParametersEnabled,separateParameters } from '~src/flow/utils/FlowParametersUtils';
import { isValidEmailTemplate } from '~src/flow/utils/FlowSubscriptionUtil';
import { FlowOutputStepInfo } from '~src/ts/models/FlowOutputStepInfo';

import { Formatters,Messages } from '../../codegen/Localize';
import { LinkedTaskFlowState } from '../state/LinkedTaskStateTypes';
import { FlowSubscriptionButtonWithDialogForLinkedTasks, FlowSubscriptionConfigurationAction } from './FlowSubscriptionButtonWithDialogForLinkedTasks';
import { LinkedTaskDeleteTaskButton } from './LinkedTaskDeleteTaskButton';
import { LinkedTaskOptionCheckboxItem, LinkedTaskOptionCheckboxList } from './LinkedTaskOptionCheckboxList';

const wrapperStyle = style({
  padding: `${TabStyles.Spacing.DefaultSpace}px ${TabStyles.Spacing.MediumSpace}px`,
});

const parameterDropdownWrapper = style({
  paddingTop: 8,
});

const optionColumnStyle = style({
  display: 'flex',
  marginTop: 16,
});

export interface FlowLinkedTaskOptionsProps {
  index: number;
  dispatch: React.Dispatch<FlowLinkedTaskOptionsAction>;
  flowLinkedTaskState: LinkedTaskFlowState;
}

export type FlowLinkedTaskOptionsAction =
  DataQualityTriggerConfigurationUIWithFlowIdAction |
  FlowSubscriptionConfigurationAction |
  { type: 'setDqwEnabled', payload: { index: number, value: LinkedTaskFlowState['dqwEnabled']}} |
  { type: 'setStopRemainingTasks', payload: { index: number, value: LinkedTaskFlowState['stopRemainingTasks']}} |
  { type: 'deleteTaskEntry', value: number } |
  { type: 'setParameterOverrides', payload: { index: number, value: { [key: string]: string }}} |
  { type: 'setRequiredParameters', payload: { index: number, value: FlowParameter[] }};

type DataQualityTriggerConfigurationUIWithFlowIdAction =
  { type: 'setEnableTrigger', payload: { index: number, value: DataQualityTriggerConfigurationUIProps['enableTrigger'] }} |
  { type: 'setIsSevere', payload: { index: number, value: DataQualityTriggerConfigurationUIProps['isSevere'] }} |
  { type: 'setMessage', payload: { index: number, value: DataQualityTriggerConfigurationUIProps['message'] }};

export function FlowLinkedTaskOptions(props: FlowLinkedTaskOptionsProps): JSX.Element {
  const [outputs, setOutputs] = React.useState<ReadonlyArray<FlowOutputStepInfo>>([]);
  const [parameters, setParameters] = React.useState<FlowParameter[]>([]);
  const [canCreateSubscription, setCanCreateSubscription] = React.useState<boolean>(false);

  const {index, flowLinkedTaskState, dispatch } = props;

  const flowOutputSubscriptionsEnabledOnSite = SessionSiteSelectors.isFlowOutputSubscriptionsEnabled(getAppStore().getState());

  React.useEffect(() => {
    Flows.getOutputStepInfos(flowLinkedTaskState.flowId).then(items => {
     setOutputs(items);
    });
    if (areFlowParametersEnabled()) {
      Flows.getById(flowLinkedTaskState.flowId).then(flow => {
        if (flow.relevantParameters !== undefined) {
          setParameters(flow.relevantParameters);
          dispatch({ type: 'setRequiredParameters', payload: { index, value: separateParameters(flow.relevantParameters).requiredParameters } });
        }
      });
    }
    if (flowOutputSubscriptionsEnabledOnSite) {
      Flows.actions(flowLinkedTaskState.flowId).then((result) => {
        setCanCreateSubscription(result.createSubscription);
      });
    }
  }, [flowLinkedTaskState.flowId, dispatch, index, flowOutputSubscriptionsEnabledOnSite]);

  const selectedRunFlowTaskInfos = React.useMemo((): readonly FlowOutputStepInfo[] => {
    if (flowLinkedTaskState.outputSelection.futureOutputsIncluded === undefined) {
      return outputs;
    }
    return outputs.filter((output) => {
      return flowLinkedTaskState.outputSelection.selectedOutputs[output.id];
    });
    }, [outputs, flowLinkedTaskState.outputSelection.selectedOutputs, flowLinkedTaskState.outputSelection.futureOutputsIncluded]
  );

  // Middle layer to convert DQW dispatch into Linked Task dispatch
  function dqwDispatch(action: DataQualityTriggerConfigurationUIAction) {
    // Linter does not like comparing the two action types here, using any to resolve
    // TODO: find typesafe typing for this action
    const updatedAction: any = {
      type: action.type,
      payload: {
        index: index,
        value: action.value
      }
    };
    dispatch(updatedAction);
  }

  const validateEmailTemplate = () => {
    if (!isValidEmailTemplate(
        flowLinkedTaskState.flowSubscriptions.shouldSendToMe,
        flowLinkedTaskState.flowSubscriptions.emailRecipients,
        flowLinkedTaskState.flowSubscriptions.subjectValue,
        flowLinkedTaskState.flowSubscriptions.shouldIncludeOutputData,
        flowLinkedTaskState.flowSubscriptions.selectedOutputForSubscription
      )) {
        // if not a valid email template when they close the dialog, uncheck the box
        dispatch({
          type: 'setShouldSendEmailWhenDone',
          payload: { index: index, value: false }
        });
      }
  };

  const dataQualityDialogProps = {
    contentType: 'flow',
    enableTrigger: flowLinkedTaskState.dqwEnabled,
    isSevere: flowLinkedTaskState.dqw.isSevere,
    message: flowLinkedTaskState.dqw.message,
    dispatch: dqwDispatch,
  };

  const succeedOptionItems: LinkedTaskOptionCheckboxItem[] = [{
    label: Messages.linkedTasksStartNextTask(),
    isMandatory: true,
  }];
  const failureOptionItems: LinkedTaskOptionCheckboxItem[] = [{
    label: Messages.linkedTasksAddDataQualityWarning(),
    isChecked: flowLinkedTaskState.dqwEnabled,
    handleClick: (value: boolean) => dispatch({ type: 'setDqwEnabled', payload: { index: index, value }}),
    actionComponent: <DataQualityTriggerUberDialog {...dataQualityDialogProps} />,
  }, {
    label: Messages.linkedTasksStopRemainingTasks(),
    isChecked: flowLinkedTaskState.stopRemainingTasks,
    handleClick: (value: boolean) => dispatch({ type: 'setStopRemainingTasks', payload: { index: index, value }}),
  }, {
    label: Messages.linkedTasksEmailMe(),
    isMandatory: true,
  }];

  function onDelete() {
    dispatch({type: 'deleteTaskEntry', value: index});
  }
  const deleteTaskButtonProps = {
    id: flowLinkedTaskState.flowId,
    onDelete: onDelete,
    style: {
      display: 'flex',
      marginLeft: 'auto',
    },
  };

  if (flowOutputSubscriptionsEnabledOnSite && !flowLinkedTaskState.linkedTaskStepId && canCreateSubscription) {
    const handleSetShouldSendEmailWhenDone = () => dispatch({ type: 'setShouldSendEmailWhenDone',
      payload: { index: index, value: !flowLinkedTaskState.shouldSendEmailWhenDone }});

    const flowOutputSubscriptionsCheckbox: LinkedTaskOptionCheckboxItem = {
      label: Messages.flowSubscriptionsCheckboxLabel(),
      isChecked: flowLinkedTaskState.shouldSendEmailWhenDone,
      handleClick: handleSetShouldSendEmailWhenDone,
      actionComponent:
      <FlowSubscriptionButtonWithDialogForLinkedTasks
        dispatch={dispatch}
        index={index}
        flowLinkedTaskState={flowLinkedTaskState}
        outputs={selectedRunFlowTaskInfos}
        onDialogClose={validateEmailTemplate}
      />
    };

    succeedOptionItems.push(flowOutputSubscriptionsCheckbox);
  }

  if (flowOutputSubscriptionsEnabledOnSite && flowLinkedTaskState.linkedTaskStepId &&
    flowLinkedTaskState.flowSubscriptions && flowLinkedTaskState.flowSubscriptions.subscriberCount) {
      const siteUrls = siteUrlsSelector(getAppStore().getState());
      const count: number = flowLinkedTaskState.flowSubscriptions.subscriberCount;
      const subscriptionCountOptionItem: LinkedTaskOptionCheckboxItem = {
        label: Messages.flowSubscriptionsCount({SUBSCRIPTIONS_COUNT: count, SUBSCRIPTIONS_COUNT_LOC:
          Formatters.formatCount(count)}),
        isMandatory: true,
        url: siteUrls.flowSubscriptionsUrl(flowLinkedTaskState.flowId),
        actionComponent: <FlowSubscriptionCountTooltip count={count} />
      };

      succeedOptionItems.push(subscriptionCountOptionItem);
    }

  function renderParameters() {
    const parametersDropdownProps: FlowParametersDropdownProps = {
      ...separateParameters(parameters),
      overrideValues: flowLinkedTaskState.parameterOverrides,
      actionHandler: (action) => dispatch({type: 'setParameterOverrides', payload: { index: index, value: action.payload }}),
    };
    return <div className={parameterDropdownWrapper}><FlowParametersDropdown {...parametersDropdownProps}/></div>;
  }

  return (
    <div className={wrapperStyle}>
      <FlowOutputSelector outputs={outputs} index={index}/>
      {areFlowParametersEnabled() && parameters.length > 0 && renderParameters()}
      <div className={optionColumnStyle}>
        <LinkedTaskOptionCheckboxList type={'succeed'} items={succeedOptionItems}/>
        <LinkedTaskOptionCheckboxList type={'fail'} items={failureOptionItems}/>
      </div>
      <LinkedTaskDeleteTaskButton {...deleteTaskButtonProps}/>
    </div>
  );
}